import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppCard,
  AppDatePicker,
  AppNoInfo,
  AppNotificationAlert,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import ModuleComsaEnvironmentalConstructionControlCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionControlCard';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalConstructionControlCardScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const locale = useSelector((state) => state.global.urlData.lang);
  const [initialValues, setInitialValues] = useState();

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionControlCard",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "constructionControlCard",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_control_card" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="environmental.design.control.sheet"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppCard
                  header={
                    <span>
                      <Translator
                        file="module-comsaEnvironmental"
                        name="general.data.work"
                      />
                    </span>
                  }
                  className="mb-2"
                >
                  <AppDatePicker
                    title={
                      <Translator file="module-intelligence" name="date" />
                    }
                    locale={locale}
                    value={values.date}
                    error={errors.date}
                    onChange={(opt) => setFieldValue("date", opt)}
                    placeholder={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="enter.date"
                      />
                    )}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                  />
                </AppCard>
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="emissions.atmosphere.emission.particles.combustion.gases"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[0]}
                  setFieldValue={(n, v) => setFieldValue(`0.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="climatic.study.site"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[1]}
                  setFieldValue={(n, v) => setFieldValue(`1.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.ventilation.systems.indoor.air.treatment"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[2]}
                  setFieldValue={(n, v) => setFieldValue(`2.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.materials.low.emission"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[3]}
                  setFieldValue={(n, v) => setFieldValue(`3.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="recommendation.use.machinery.dust.collectors"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[4]}
                  setFieldValue={(n, v) => setFieldValue(`4.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="measures.minimize.dust.generation.during.execution"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[5]}
                  setFieldValue={(n, v) => setFieldValue(`5.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="measures.reduce.lighting.impact"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[6]}
                  setFieldValue={(n, v) => setFieldValue(`6.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="recommendation.use.vehicles.machinery.low.gas.emissions"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="noises.and.vibrations.noise"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[7]}
                  setFieldValue={(n, v) => setFieldValue(`7.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="conducting.acoustic.studies.noise.map"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[8]}
                  setFieldValue={(n, v) => setFieldValue(`8.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.corrective.measures.noise.infrastructure.sound.shielding"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[9]}
                  setFieldValue={(n, v) => setFieldValue(`9.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.materials.low.acoustic.emission"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[10]}
                  setFieldValue={(n, v) => setFieldValue(`10.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.corrective.measures.noise.minimization.construction"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[11]}
                  setFieldValue={(n, v) => setFieldValue(`11.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.machinery.equipment.low.sonic.level"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[12]}
                  setFieldValue={(n, v) => setFieldValue(`12.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.noisy.activities.daytime.hours"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[13]}
                  setFieldValue={(n, v) => setFieldValue(`13.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="vibration.study"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[14]}
                  setFieldValue={(n, v) => setFieldValue(`14.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.antivibration.elements.infrastructure"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[15]}
                  setFieldValue={(n, v) => setFieldValue(`15.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.materials.low.vibratory.emission"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[16]}
                  setFieldValue={(n, v) => setFieldValue(`16.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.protect.vibration.receiving.points"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[17]}
                  setFieldValue={(n, v) => setFieldValue(`17.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.machinery.equipment.low.vibratory.level"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="liquid.discharges.water.discharges"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[19]}
                  setFieldValue={(n, v) => setFieldValue(`19.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.cleaning.maintenance.areas.machinery"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[20]}
                  setFieldValue={(n, v) => setFieldValue(`20.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.appropriate.waste.fuel.storage.areas"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[21]}
                  setFieldValue={(n, v) => setFieldValue(`21.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="designing.purification.systems.settling.basins"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[22]}
                  setFieldValue={(n, v) => setFieldValue(`22.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.system.containment.uncontrolled.discharges"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="channels"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[23]}
                  setFieldValue={(n, v) => setFieldValue(`23.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="protection.river.systems.territory.classification.drainage.sizing"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[24]}
                  setFieldValue={(n, v) => setFieldValue(`24.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.protect.subterranean.water.resources"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[25]}
                  setFieldValue={(n, v) => setFieldValue(`25.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.water.quality.protection.systems.water.purification.systems"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[26]}
                  setFieldValue={(n, v) => setFieldValue(`26.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="measures.river.systems.settling.basins.sediment.etc"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="marine.coastal.environment.alteration.water.seabed.characteristics"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[27]}
                  setFieldValue={(n, v) => setFieldValue(`27.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="marine.ecosystem.inventory"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[28]}
                  setFieldValue={(n, v) => setFieldValue(`28.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.marine.dynamics.behavior"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[29]}
                  setFieldValue={(n, v) => setFieldValue(`29.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.navigation.dock.avoiding.disturbances.marine"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[30]}
                  setFieldValue={(n, v) => setFieldValue(`30.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="ensuring.new.technologies.used.not.harmful.environment"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[31]}
                  setFieldValue={(n, v) => setFieldValue(`31.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="adopting.measures.prevent.correct.actions.erosion.substrate"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[32]}
                  setFieldValue={(n, v) => setFieldValue(`32.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.execution.work.behavior.marine.dynamics.current.systems"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="affectation.vegetation.damage.removal.vegetation"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[33]}
                  setFieldValue={(n, v) => setFieldValue(`33.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="inventory.existing.flora.protected.species"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[34]}
                  setFieldValue={(n, v) => setFieldValue(`34.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.protect.flora.during.execution.work"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[35]}
                  setFieldValue={(n, v) => setFieldValue(`35.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.ensure.permeability.infrastructure.wildlife"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[36]}
                  setFieldValue={(n, v) => setFieldValue(`36.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.vegetal.regeneration.area"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="danger">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="affectation.fauna.disturbance.wildlife"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[37]}
                  setFieldValue={(n, v) => setFieldValue(`37.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="inventory.existing.fauna.nesting.areas.faunal.flows"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[38]}
                  setFieldValue={(n, v) => setFieldValue(`38.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="identifying.protected.areas.community"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[39]}
                  setFieldValue={(n, v) => setFieldValue(`39.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="protection.measures.populations.execution.work"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[40]}
                  setFieldValue={(n, v) => setFieldValue(`40.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.ensure.permeability.wildlife"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[41]}
                  setFieldValue={(n, v) => setFieldValue(`41.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.vegetal.regeneration.area"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.consumption.raw.materials"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[42]}
                  setFieldValue={(n, v) => setFieldValue(`42.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.sustainable.materials.products"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.water.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[43]}
                  setFieldValue={(n, v) => setFieldValue(`43.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.water.consumption"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[44]}
                  setFieldValue={(n, v) => setFieldValue(`44.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.low.consumption"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[45]}
                  setFieldValue={(n, v) => setFieldValue(`45.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.water.reuse"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.fuel.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[46]}
                  setFieldValue={(n, v) => setFieldValue(`46.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.fuel.consumption"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[47]}
                  setFieldValue={(n, v) => setFieldValue(`47.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.machinery.equipment.low.sonic.level"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="natural.resources.electricity.consumption"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[48]}
                  setFieldValue={(n, v) => setFieldValue(`48.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promoting.minimization.electrical.consumption"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[49]}
                  setFieldValue={(n, v) => setFieldValue(`49.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="choice.construction.methods.low.electrical.consumption"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.urban.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[50]}
                  setFieldValue={(n, v) => setFieldValue(`50.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.waste.choice.materials"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.inert.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[51]}
                  setFieldValue={(n, v) => setFieldValue(`51.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.waste.separation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.non.hazardous.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[52]}
                  setFieldValue={(n, v) => setFieldValue(`52.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.measures.reuse.valorization.disposal.waste"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.hazardous.waste"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[53]}
                  setFieldValue={(n, v) => setFieldValue(`53.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.construction.demolition.waste.management"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="order.cleanliness"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[54]}
                  setFieldValue={(n, v) => setFieldValue(`54.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="clean.materials.not.generate.dust.waste"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[55]}
                  setFieldValue={(n, v) => setFieldValue(`55.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="clean.construction.methods.establishing.cleaning.systems"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="landscape.visual.impact"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[56]}
                  setFieldValue={(n, v) => setFieldValue(`56.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="identify.areas.high.landscape.quality"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[57]}
                  setFieldValue={(n, v) => setFieldValue(`57.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="treatment.visually.fragile.points.location.accessibility.frequentation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[58]}
                  setFieldValue={(n, v) => setFieldValue(`58.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="corrective.measures.reduce.visual.impact"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="primary">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="historical.artistic.heritage.affectation.heritage"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[59]}
                  setFieldValue={(n, v) => setFieldValue(`59.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="conducting.archaeological.heritage.study"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[60]}
                  setFieldValue={(n, v) => setFieldValue(`60.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="establishing.program.protection.cultural.archaeological"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="socioeconomic.impact.economic.activity"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[61]}
                  setFieldValue={(n, v) => setFieldValue(`61.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="promote.hiring.local.labor"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[62]}
                  setFieldValue={(n, v) => setFieldValue(`62.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.economic.activity.area"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[63]}
                  setFieldValue={(n, v) => setFieldValue(`63.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="appropriate.valuation.land.properties.occupied"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6
                    className="mb-0"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="aspect"
                      />
                    )}
                  >
                    <Translator
                      file="module-comsaEnvironmental"
                      name="socioeconomic.impact.impact.on.mobility"
                    />
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[64]}
                  setFieldValue={(n, v) => setFieldValue(`64.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.impact.on.road.traffic"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[65]}
                  setFieldValue={(n, v) => setFieldValue(`65.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.impact.on.pedestrian.traffic"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[66]}
                  setFieldValue={(n, v) => setFieldValue(`66.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="study.impact.on.rail.traffic"
                    />
                  }
                />
                <ModuleComsaEnvironmentalConstructionControlCard
                  values={values?.[67]}
                  setFieldValue={(n, v) => setFieldValue(`67.${n}`, v)}
                  description={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="planning.closures.diversions.during.construction.phase"
                    />
                  }
                />
                <div className="mb-3" />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
